import React, { useState, useEffect } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  Button,
  InputAdornment,
  Popover,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import SortRoundedIcon from "@mui/icons-material/SortRounded";

const toTitleCase = (str) =>
  str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const SORT_OPTIONS = [{ value: "created_at", label: "Created Date" }];

const FilterComponent = ({ filterOptions = {}, onApply, onClear }) => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [showMore, setShowMore] = useState({});
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("ASC");

  // Initialize selected filters
  useEffect(() => {
    if (Object.keys(filterOptions).length > 0) {
      setSelectedFilters((prev) => {
        const initialFilters = {};
        Object.keys(filterOptions).forEach((key) => {
          // Preserve existing selections if they exist
          if (prev[key]) {
            initialFilters[key] = prev[key];
          } else {
            // Initialize new filters
            initialFilters[key] =
              typeof filterOptions[key] === "string"
                ? filterOptions[key].split("|")
                : [];
          }
        });
        return initialFilters;
      });
    }
  }, [filterOptions]);

  const handleFilterChange = (category, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((item) => item !== value)
        : [...prev[category], value],
    }));
  };

  const handleApplyFilters = () => {
    const filterData = Object.entries(selectedFilters).reduce(
      (acc, [key, values]) => {
        if (values.length > 0) {
          acc[key] = values.join("|");
        }
        return acc;
      },
      {}
    );

    // Add sorting parameters
    filterData.sort_by = sortBy;
    filterData.sort_order = sortOrder;

    onApply(searchQuery, filterData);
    // Don't reset the selectedFilters here
  };

const handleSortChange = (field, value) => {
  // Update state immediately using functional updates
  if (field === "sortBy") {
    setSortBy(value);
  } else {
    setSortOrder(value);
  }

  // Prepare filters with updated values
  const filterData = Object.entries(selectedFilters).reduce(
    (acc, [key, values]) => {
      if (values.length > 0) acc[key] = values.join("|");
      return acc;
    },
    {}
  );

  // Use current state values for the API call
  filterData.sort_by = field === "sortBy" ? value : sortBy;
  filterData.sort_order = field === "sortOrder" ? value : sortOrder;

  // Immediately apply with updated values
  onApply(searchQuery, filterData);
};

 

  const handleClearFilters = () => {
    const emptyFilters = {};
    Object.keys(filterOptions).forEach((key) => {
      emptyFilters[key] = [];
    });

    setSelectedFilters(emptyFilters);
    setSearchQuery("");
    setSortBy("created_at");
    setSortOrder("ASC");
    onClear();
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 3,
        p: 3,
      }}
    >
      {/* Search and Sort Header */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2 }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search jobs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{ textTransform: "none" }}
                  >
                    Search
                  </Button>
                </Stack>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        <Box
          onClick={(e) => setSortAnchorEl(e.currentTarget)}
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <SortRoundedIcon fontSize="large" />
        </Box>
      </Box>

      {/* Sort Popover */}
      <Popover
        open={Boolean(sortAnchorEl)}
        anchorEl={sortAnchorEl}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Sorting Options
          </Typography>

          {/* <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Sort By
            </Typography>
            <Select
              value={sortBy}
              onChange={(e) => handleSortChange("sortBy", e.target.value)}
              fullWidth
              size="small"
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box> */}

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Order
            </Typography>
            <Select
              value={sortOrder}
              onChange={(e) => handleSortChange("sortOrder", e.target.value)}
              fullWidth
              size="small"
            >
              {/* <MenuItem value="">Select</MenuItem> */}
              <MenuItem value="ASC">Oldest</MenuItem>
              <MenuItem value="DESC">Most Recent</MenuItem>
            </Select>
          </Box>
        </Box>
      </Popover>

      {/* Filter Sections */}
      <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
        {Object.entries(filterOptions).map(([category, options]) => {
          if (location.pathname === "/alljobs" && category === "company_name")
            return null;

          const validOptions = Array.isArray(options) ? options : [];
          const isExpanded = showMore[category] || false;
          const visibleOptions = isExpanded
            ? validOptions
            : validOptions.slice(0, 5);
          const hasMore = validOptions.length > 5;

          return (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {toTitleCase(category)}
              </Typography>

              <Box sx={{ display: "grid", gap: 0.5 }}>
                {visibleOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={selectedFilters[category]?.includes(option)}
                        onChange={() => handleFilterChange(category, option)}
                        size="small"
                        color="primary"
                        sx={{ py: 0 }}
                      />
                    }
                    label={<Typography variant="body2">{option}</Typography>}
                    sx={{ ml: 0, mr: 0 }}
                  />
                ))}
              </Box>

              {hasMore && (
                <Button
                  size="small"
                  onClick={() =>
                    setShowMore((prev) => ({
                      ...prev,
                      [category]: !prev[category],
                    }))
                  }
                  sx={{ mt: 0.5, textTransform: "none" }}
                >
                  {isExpanded
                    ? "Show Less"
                    : `${validOptions.length - 5} More...`}
                </Button>
              )}
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          fullWidth
          sx={{ textTransform: "none" }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          fullWidth
          sx={{ textTransform: "none" }}
        >
          Clear All
        </Button>
      </Box>
    </Box>
  );
};

export default FilterComponent;
